import React, { useContext, useState, useRef, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { ApplicationContext } from "../../context/GlobalState";
import { t, extractLocaleFromPath } from "../../i18n";
import doctorsData from "../../data/team/doctors.json";
import {
  FaUser,
  FaUserMd,
  FaGavel,
  FaPaperclip,
  FaCheck,
  FaChevronRight,
  FaChevronLeft,
  FaPhone,
  FaMapMarkerAlt,
} from "react-icons/fa";
import HreflangTags from "../../components/HreflangTags/HreflangTags";
import "./RequestAppointment.css";

const BODY_PART_KEYS = [
  "bodyPart.neckBack",
  "bodyPart.shoulder",
  "bodyPart.knee",
  "bodyPart.ankle",
  "bodyPart.elbow",
  "bodyPart.wrist",
  "bodyPart.headacheConcussion",
  "bodyPart.neuro",
  "bodyPart.other",
];

const MAX_ATTACHMENTS = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const STEPS = [
  { key: "step1", icon: FaUser, label: "form.step1" },
  { key: "step2", icon: FaUserMd, label: "form.step2" },
  { key: "step3", icon: FaGavel, label: "form.step3" },
  { key: "step4", icon: FaPaperclip, label: "form.step4" },
];

const initialFormData = {
  // Step 1 - Patient Details
  smsConsent: false,
  requestedProvider: "",
  patientFirstName: "",
  patientLastName: "",
  patientEmail: "",
  patientPhone: "",
  patientDob: "",
  dateOfInjury: "",
  patientAddress: "",
  bodyParts: [],
  moreInformation: "",
  // Step 2 - Referral Details
  referringGroup: "",
  referringName: "",
  referringEmail: "",
  referringPhone: "",
  // Step 3 - Attorney Details
  attorneyFirm: "",
  attorneyName: "",
  attorneyEmail: "",
  attorneyPhone: "",
  caseManagerName: "",
  caseManagerEmail: "",
  // Step 4 - Attachments
  attachments: [],
  attachmentDescription: "",
};

export default function RequestAppointment() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;
  const baseDomain = brand?.domain || state.client?.domain || 'gaspineortho.com';
  const brandKey = brand?.brandKey || 'gso';

  // Brand-scoped provider list. Apex brands filter by state; GSO uses legacy site key.
  const brandSiteKey = brandKey === 'gso' ? 'gso' : (brandKey === 'apex-nc' || brandKey === 'apex-tn') ? 'apex' : null;
  const brandState = brand?.state || null;
  const isApex = brandKey === 'apex-nc' || brandKey === 'apex-tn';
  const brandDoctors = useMemo(() => {
    if (isApex) {
      return doctorsData.filter(d => {
        if (Array.isArray(d.states)) return d.states.includes(brandState);
        if (Array.isArray(d.sites)) return d.sites.includes('apex');
        return d.site === 'apex';
      });
    }
    return brandSiteKey
      ? doctorsData.filter(d => Array.isArray(d.sites) ? d.sites.includes(brandSiteKey) : d.site === brandSiteKey)
      : doctorsData;
  }, [isApex, brandState, brandSiteKey]);

  // Resolve ?provider=<slug-or-name> from URL → display name
  const providerParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('provider');
    if (!raw) return '';
    const decoded = decodeURIComponent(raw).trim();
    const lowered = decoded.toLowerCase();
    const match = brandDoctors.find(d =>
      d.id?.toLowerCase() === lowered ||
      d.name?.toLowerCase() === lowered ||
      d.name?.toLowerCase().replace(/^dr\.?\s+/i, '') === lowered
    );
    return match ? match.name : decoded;
  }, [location.search, brandDoctors]);

  const canonicalUrl = `https://${baseDomain}/appointment`;
  const pageTitle = `${t("form.scheduleAppointment", locale)} | ${state.client.name}`;
  const pageDesc = t("form.heroSubtitle", locale);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(() => ({
    ...initialFormData,
    requestedProvider: providerParam || '',
  }));
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const fileInputRef = useRef(null);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const toggleBodyPart = (part) => {
    setFormData((prev) => ({
      ...prev,
      bodyParts: prev.bodyParts.includes(part)
        ? prev.bodyParts.filter((p) => p !== part)
        : [...prev.bodyParts, part],
    }));
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    const remaining = MAX_ATTACHMENTS - formData.attachments.length;
    const accepted = files.slice(0, Math.max(0, remaining));
    for (const file of accepted) {
      const localId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const placeholder = {
        localId, name: file.name, size: file.size, status: 'uploading',
        key: null, contentType: file.type || '', error: null,
      };
      setFormData((prev) => ({ ...prev, attachments: [...prev.attachments, placeholder] }));
      try {
        if (file.size > MAX_FILE_BYTES) throw new Error('file_too_large');
        const fd = new FormData();
        fd.append('file', file);
        fd.append('brand', brandKey);
        const resp = await fetch('/api/upload-attachment', { method: 'POST', body: fd });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(json.error || ('http_' + resp.status));
        setFormData((prev) => ({
          ...prev,
          attachments: prev.attachments.map((a) => a.localId === localId
            ? { ...a, status: 'done', key: json.key, filename: json.filename, size: json.size, contentType: json.contentType }
            : a),
        }));
      } catch (err) {
        setFormData((prev) => ({
          ...prev,
          attachments: prev.attachments.map((a) => a.localId === localId
            ? { ...a, status: 'error', error: String(err.message || err) }
            : a),
        }));
      }
    }
  };

  const removeFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const uploadingCount = (formData.attachments || []).filter((a) => a.status === 'uploading').length;

  const validateStep = (step) => {
    const errs = {};
    if (step === 0) {
      if (!formData.patientFirstName.trim()) errs.patientFirstName = true;
      if (!formData.patientLastName.trim()) errs.patientLastName = true;
      if (!formData.patientPhone.trim()) errs.patientPhone = true;
      if (formData.patientEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.patientEmail))
        errs.patientEmail = 'invalid';
    }
    if (step === 1) {
      if (formData.referringEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.referringEmail))
        errs.referringEmail = 'invalid';
    }
    if (step === 2) {
      if (formData.attorneyEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.attorneyEmail))
        errs.attorneyEmail = 'invalid';
      if (formData.caseManagerEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.caseManagerEmail))
        errs.caseManagerEmail = 'invalid';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const goToStep = (index) => {
    if (index < currentStep) {
      setCurrentStep(index);
    } else if (index === currentStep + 1 && validateStep(currentStep)) {
      setCurrentStep(index);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;
    if (submitting) return;
    setSubmitting(true);
    setSubmitError('');

    // Build payload: forward all form fields plus normalized
    // {firstName,lastName,phone,email,notes} the backend expects, plus brand.
    // bodyParts is sent as a comma-joined string for readable email rendering.
    const payload = {
      brand: brandKey,
      firstName: formData.patientFirstName,
      lastName: formData.patientLastName,
      phone: formData.patientPhone,
      email: formData.patientEmail,
      notes: formData.moreInformation,
      requestedProvider: formData.requestedProvider,
      // Pass-through (rendered as additional fields by backend)
      smsConsent: formData.smsConsent ? 'yes' : 'no',
      patientDob: formData.patientDob,
      dateOfInjury: formData.dateOfInjury,
      patientAddress: formData.patientAddress,
      bodyParts: (formData.bodyParts || []).map((k) => t(k, locale)).join(', '),
      referringGroup: formData.referringGroup,
      referringName: formData.referringName,
      referringEmail: formData.referringEmail,
      referringPhone: formData.referringPhone,
      attorneyFirm: formData.attorneyFirm,
      attorneyName: formData.attorneyName,
      attorneyEmail: formData.attorneyEmail,
      attorneyPhone: formData.attorneyPhone,
      caseManagerName: formData.caseManagerName,
      caseManagerEmail: formData.caseManagerEmail,
      attachmentDescription: formData.attachmentDescription,
      attachmentCount: (formData.attachments || []).length,
      attachments: (formData.attachments || [])
        .filter((a) => a && a.status === 'done' && a.key)
        .map((a) => ({ key: a.key, filename: a.filename, size: a.size, contentType: a.contentType })),
      // Honeypot — must remain empty for real submissions
      website: e.target.elements?.website?.value || '',
    };

    try {
      const resp = await fetch('/api/request-appointment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error('http_' + resp.status);
      setSubmitted(true);
    } catch (err) {
      const phone = brand?.phone || state.client?.phone || '';
      setSubmitError(
        "We couldn't submit your request right now. Please call " +
        (phone || 'our office') +
        ' directly or try again in a moment.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="appt-page">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDesc} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDesc} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:image" content={`https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`} />
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
        <HreflangTags baseDomain={baseDomain} pathWithoutLocale="/appointment" />
        <div className="appt-success">
          <div className="appt-success-inner">
            <div className="appt-success-icon">
              <FaCheck />
            </div>
            <h2>{t("form.success", locale)}</h2>
            <p>
              {t("form.successMessage", locale, { phone: state.client.phone })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="appt-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale="/appointment" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "MedicalBusiness",
        name: state.client.name,
        url: canonicalUrl,
        telephone: state.client.phone,
        availableService: { "@type": "MedicalTherapy", name: t("form.scheduleAppointment", locale) }
      }) }} />

      {/* Hero */}
      <section className="appt-hero">
        <div className="container">
          <h1>{t("form.scheduleAppointment", locale)}</h1>
          <p>{t("form.heroSubtitle", locale)}</p>
        </div>
      </section>

      <section className="appt-content section-padding">
        <div className="container">
          <div className="appt-layout">
            {/* Sidebar info */}
            <aside className="appt-sidebar">
              <div className="appt-sidebar-card">
                <h3>{t("form.needHelp", locale)}</h3>
                <div className="appt-sidebar-item">
                  <FaPhone />
                  <div>
                    <strong>{t("cta.callUs", locale)}</strong>
                    <a href={`tel:${brand?.phone || state.client.phone}`}>
                      {brand?.phone || state.client.phone}
                    </a>
                  </div>
                </div>
                <div className="appt-sidebar-item">
                  <FaMapMarkerAlt />
                  <div>
                    <strong>
                      {t("form.locationsCount", locale, { count: (brand?.physicalSites?.length || state.client?.physicalSites?.length || 0) })}
                    </strong>
                    <span>{t("form.locationsRegion", locale)}</span>
                  </div>
                </div>
              </div>
            </aside>

            {/* Form */}
            <div className="appt-form-area">
              {/* Step indicator */}
              <div className="step-indicator">
                {STEPS.map((step, i) => {
                  const Icon = step.icon;
                  const isActive = i === currentStep;
                  const isComplete = i < currentStep;
                  return (
                    <button
                      key={step.key}
                      type="button"
                      className={`step-dot ${isActive ? "active" : ""} ${isComplete ? "complete" : ""}`}
                      onClick={() => goToStep(i)}
                    >
                      <span className="step-dot-icon">
                        {isComplete ? <FaCheck /> : <Icon />}
                      </span>
                      <span className="step-dot-label">
                        {t(step.label, locale)}
                      </span>
                    </button>
                  );
                })}
              </div>

              <form onSubmit={handleSubmit}>
                {/* Honeypot — bots fill this; real users never see it */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  defaultValue=""
                  style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                  aria-hidden="true"
                />
                {/* STEP 1: Patient Details */}
                {currentStep === 0 && (
                  <div className="form-step">
                    <h2>{t("form.step1", locale)}</h2>

                    <label className="checkbox-label sms-consent">
                      <input
                        type="checkbox"
                        checked={formData.smsConsent}
                        onChange={(e) =>
                          updateField("smsConsent", e.target.checked)
                        }
                      />
                      <span>{t("form.smsConsent", locale)}</span>
                    </label>

                    <div className="form-group">
                      <label>{t("form.requestedProvider", locale)}</label>
                      <input
                        type="text"
                        value={formData.requestedProvider}
                        onChange={(e) =>
                          updateField("requestedProvider", e.target.value)
                        }
                        placeholder={t("form.requestedProviderPlaceholder", locale)}
                        list="requested-provider-options"
                      />
                      <datalist id="requested-provider-options">
                        {brandDoctors.map(d => (
                          <option key={d.id} value={d.name} />
                        ))}
                      </datalist>
                      <small className="field-help">{t("form.requestedProviderHelp", locale)}</small>
                    </div>

                    <div className="form-row">
                      <div
                        className={`form-group ${errors.patientFirstName ? "has-error" : ""}`}
                      >
                        <label>{t("form.patientFirstName", locale)} *</label>
                        <input
                          type="text"
                          value={formData.patientFirstName}
                          onChange={(e) =>
                            updateField("patientFirstName", e.target.value)
                          }
                          placeholder={t("form.patientFirstName", locale)}
                        />
                      </div>
                      <div
                        className={`form-group ${errors.patientLastName ? "has-error" : ""}`}
                      >
                        <label>{t("form.patientLastName", locale)} *</label>
                        <input
                          type="text"
                          value={formData.patientLastName}
                          onChange={(e) =>
                            updateField("patientLastName", e.target.value)
                          }
                          placeholder={t("form.patientLastName", locale)}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className={`form-group ${errors.patientEmail ? "has-error" : ""}`}>
                        <label>{t("form.patientEmail", locale)}</label>
                        <input
                          type="email"
                          value={formData.patientEmail}
                          onChange={(e) =>
                            updateField("patientEmail", e.target.value)
                          }
                          placeholder={t("form.patientEmail", locale)}
                        />
                        {errors.patientEmail && (
                          <span className="field-error-msg">{t("form.emailInvalid", locale)}</span>
                        )}
                      </div>
                      <div
                        className={`form-group ${errors.patientPhone ? "has-error" : ""}`}
                      >
                        <label>{t("form.patientPhone", locale)} *</label>
                        <input
                          type="tel"
                          value={formData.patientPhone}
                          onChange={(e) =>
                            updateField("patientPhone", e.target.value)
                          }
                          placeholder={t("form.patientPhone", locale)}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>{t("form.patientDob", locale)}</label>
                        <input
                          type="date"
                          value={formData.patientDob}
                          onChange={(e) =>
                            updateField("patientDob", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>{t("form.dateOfInjury", locale)}</label>
                        <input
                          type="date"
                          value={formData.dateOfInjury}
                          onChange={(e) =>
                            updateField("dateOfInjury", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>{t("form.patientAddress", locale)}</label>
                      <input
                        type="text"
                        value={formData.patientAddress}
                        onChange={(e) =>
                          updateField("patientAddress", e.target.value)
                        }
                        placeholder={t("form.patientAddress", locale)}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t("form.bodyPart", locale)}</label>
                      <div className="checkbox-grid">
                        {BODY_PART_KEYS.map((partKey) => (
                          <label key={partKey} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.bodyParts.includes(partKey)}
                              onChange={() => toggleBodyPart(partKey)}
                            />
                            <span>{t(partKey, locale)}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>{t("form.moreInfo", locale)}</label>
                      <textarea
                        value={formData.moreInformation}
                        onChange={(e) =>
                          updateField("moreInformation", e.target.value)
                        }
                        placeholder={t("form.moreInfoPlaceholder", locale)}
                        rows={3}
                      />
                    </div>
                  </div>
                )}

                {/* STEP 2: Referral Details */}
                {currentStep === 1 && (
                  <div className="form-step">
                    <h2>{t("form.step2", locale)}</h2>
                    <p className="step-subtitle">
                      {t("form.step2Subtitle", locale)}
                    </p>

                    <div className="form-row">
                      <div className="form-group">
                        <label>{t("form.referringGroup", locale)}</label>
                        <input
                          type="text"
                          value={formData.referringGroup}
                          onChange={(e) =>
                            updateField("referringGroup", e.target.value)
                          }
                          placeholder={t("form.referringGroup", locale)}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t("form.referringName", locale)}</label>
                        <input
                          type="text"
                          value={formData.referringName}
                          onChange={(e) =>
                            updateField("referringName", e.target.value)
                          }
                          placeholder={t("form.referringName", locale)}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>{t("form.referringEmail", locale)}</label>
                        <input
                          type="email"
                          value={formData.referringEmail}
                          onChange={(e) =>
                            updateField("referringEmail", e.target.value)
                          }
                          placeholder={t("form.referringEmail", locale)}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t("form.referringPhone", locale)}</label>
                        <input
                          type="tel"
                          value={formData.referringPhone}
                          onChange={(e) =>
                            updateField("referringPhone", e.target.value)
                          }
                          placeholder={t("form.referringPhone", locale)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3: Attorney Details */}
                {currentStep === 2 && (
                  <div className="form-step">
                    <h2>{t("form.step3", locale)}</h2>
                    <p className="step-subtitle">
                      {t("form.step3Subtitle", locale)}
                    </p>

                    <div className="form-row">
                      <div className="form-group">
                        <label>{t("form.attorneyFirm", locale)}</label>
                        <input
                          type="text"
                          value={formData.attorneyFirm}
                          onChange={(e) =>
                            updateField("attorneyFirm", e.target.value)
                          }
                          placeholder={t("form.attorneyFirm", locale)}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t("form.attorneyName", locale)}</label>
                        <input
                          type="text"
                          value={formData.attorneyName}
                          onChange={(e) =>
                            updateField("attorneyName", e.target.value)
                          }
                          placeholder={t("form.attorneyName", locale)}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>{t("form.attorneyEmail", locale)}</label>
                        <input
                          type="email"
                          value={formData.attorneyEmail}
                          onChange={(e) =>
                            updateField("attorneyEmail", e.target.value)
                          }
                          placeholder={t("form.attorneyEmail", locale)}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t("form.attorneyPhone", locale)}</label>
                        <input
                          type="tel"
                          value={formData.attorneyPhone}
                          onChange={(e) =>
                            updateField("attorneyPhone", e.target.value)
                          }
                          placeholder={t("form.attorneyPhone", locale)}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>{t("form.caseManagerName", locale)}</label>
                        <input
                          type="text"
                          value={formData.caseManagerName}
                          onChange={(e) =>
                            updateField("caseManagerName", e.target.value)
                          }
                          placeholder={t("form.caseManagerName", locale)}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t("form.caseManagerEmail", locale)}</label>
                        <input
                          type="email"
                          value={formData.caseManagerEmail}
                          onChange={(e) =>
                            updateField("caseManagerEmail", e.target.value)
                          }
                          placeholder={t("form.caseManagerEmail", locale)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 4: Attachments */}
                {currentStep === 3 && (
                  <div className="form-step">
                    <h2>{t("form.step4", locale)}</h2>
                    <p className="step-subtitle">
                      {t("form.step4Subtitle", locale)}
                    </p>

                    <div className="form-group">
                      <label>{t("form.attachments", locale)}</label>
                      <div
                        className="file-drop-zone"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <FaPaperclip />
                        <span>{t("form.uploadPrompt", locale)}</span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                      </div>
                      {formData.attachments.length > 0 && (
                        <div className="file-list">
                          {formData.attachments.map((file, i) => (
                            <div key={file.localId || i} className="file-item">
                              <span>
                                {file.filename || file.name} ({(file.size / 1024).toFixed(1)} KB)
                                {file.status === 'uploading' && <em style={{ marginLeft: 8, color: '#888' }}>uploading…</em>}
                                {file.status === 'error' && <em style={{ marginLeft: 8, color: '#b03030' }}>upload failed ({file.error})</em>}
                                {file.status === 'done' && <em style={{ marginLeft: 8, color: '#3a8a3a' }}>✓</em>}
                              </span>
                              <button
                                type="button"
                                className="file-remove"
                                onClick={() => removeFile(i)}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>{t("form.attachmentDesc", locale)}</label>
                      <textarea
                        value={formData.attachmentDescription}
                        onChange={(e) =>
                          updateField("attachmentDescription", e.target.value)
                        }
                        placeholder={t("form.attachmentDesc", locale)}
                        rows={3}
                      />
                    </div>
                  </div>
                )}

                {/* Navigation buttons */}
                <div className="form-nav">
                  {currentStep > 0 && (
                    <button
                      type="button"
                      className="btn-back"
                      onClick={prevStep}
                    >
                      <FaChevronLeft /> {t("form.back", locale)}
                    </button>
                  )}
                  <div className="form-nav-spacer" />
                  {currentStep < STEPS.length - 1 ? (
                    <button
                      type="button"
                      className="btn-next"
                      onClick={nextStep}
                    >
                      {t("form.next", locale)} <FaChevronRight />
                    </button>
                  ) : (
                    <button type="submit" className="btn-submit" disabled={submitting || uploadingCount > 0}>
                      {submitting ? 'Submitting…' : (uploadingCount > 0 ? `Uploading ${uploadingCount}…` : t("form.scheduleAppointment", locale))}
                    </button>
                  )}
                </div>
                {submitError && (
                  <div className="appt-submit-error" role="alert" style={{ marginTop: 16, padding: '12px 16px', border: '1px solid #d4787a', background: '#fdecec', color: '#7a2424', borderRadius: 6 }}>
                    {submitError}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
